import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useMe } from "@/context/MeContext";

const PAD = 8;

// Finds the step's element (it may still be loading), keeps it in view and reports its box.
const useTarget = (sel) => {
  const [box, setBox] = useState(null);
  useEffect(() => {
    let el = null;
    let tries = 0;
    const measure = () => { if (el) { const r = el.getBoundingClientRect(); setBox({ top: r.top, left: r.left, width: r.width, height: r.height }); } };
    const find = setInterval(() => {
      el = document.querySelector(sel);
      if (el || ++tries > 40) {
        clearInterval(find);
        if (!el) return;
        if (el.offsetHeight > window.innerHeight * 0.55) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80 });
        else el.scrollIntoView({ block: "center" });
        setTimeout(measure, 350);
      }
    }, 200);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => { clearInterval(find); window.removeEventListener("scroll", measure, true); window.removeEventListener("resize", measure); };
  }, [sel]);
  return box;
};

export const useTourDone = () => {
  const { me, refresh } = useMe();
  const done = !!me?.settings?.live_tour_done;
  const finish = useCallback(async () => { await api.patch("/me", { settings: { live_tour_done: true } }).catch(() => {}); refresh().catch(() => {}); }, [refresh]);
  return { ready: !!me, done, finish };
};

export const LiveTour = ({ steps, start = 0, onDone, offset = 0, total }) => {
  const [i, setI] = useState(start);
  const s = steps[i];
  const box = useTarget(s.sel);
  const last = i === steps.length - 1;
  const next = () => (s.next ? s.next() : last ? onDone() : setI(i + 1));
  const below = box && box.top + box.height + 240 < window.innerHeight;
  const tall = box && box.height > window.innerHeight * 0.55;
  const cardTop = !box ? window.innerHeight / 2 - 100 : tall ? window.innerHeight - 236 : below ? box.top + box.height + PAD + 12 : Math.max(12, box.top - PAD - 12 - 200);
  return createPortal(
    <div className="fixed inset-0 z-[60]" data-testid="live-tour">
      {box ? <div className="pointer-events-none fixed rounded-2xl ring-2 ring-white transition-all duration-300"
        style={{ top: box.top - PAD, left: box.left - PAD, width: box.width + 2 * PAD, height: box.height + 2 * PAD, boxShadow: "0 0 0 9999px rgba(15,23,42,0.6)" }} />
        : <div className="fixed inset-0 bg-slate-900/60" />}
      <div className="fixed left-1/2 w-[min(92vw,380px)] -translate-x-1/2 rounded-2xl bg-white p-5 shadow-2xl transition-all duration-300" style={{ top: cardTop }} data-testid={`live-tour-step-${offset + i}`}>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--facts))]">Step {offset + i + 1} of {total || steps.length}</p>
        <p className="mt-1 font-serif text-xl leading-snug">{s.title}</p>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{s.text}</p>
        <div className="mt-4 flex items-center gap-2">
          <Button variant="ghost" size="sm" className="rounded-full" onClick={onDone} data-testid="live-tour-skip">Skip tour</Button>
          <Button size="sm" className="ml-auto rounded-full" onClick={next} data-testid="live-tour-next">{s.cta || (last ? "Done" : "Next")}</Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
